import { useMemo, useRef } from 'react';
import type { Lens } from '@hookform/lenses';
import { Big } from 'big.js';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { DEFAULT_PERCENTAGE_MASK } from '@interxfi/design-system/components/input/text-input/masks';
import { useIsElementVisible } from '@interxfi/design-system/util/visibility';
import { type IxcRegistrationV1BeneficialOwnerDirect, IxcRegistrationV1ProfileStatus } from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import {
  buildProfileListQuery,
  RegistrationProfileComboboxWithLens,
} from '../../../../../../../components/data-combobox-input/entity-config/registration-profile';
import { AppSwitchInputWithLens } from '../../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../../components/form/app-text-input';
import { getNumericStringValidator } from '../../../../../../../util/validation';

export const directBeneficialOwnerControlPersonFormSchema = z.object({
  kind: z.literal('directBeneficialOwner'),
  value: z.object({
    profileId: z.string().nonempty('Profile is required'),
    signer: z.boolean(),
    ownership: getNumericStringValidator('Ownership Percentage', { gte: 25, lte: 100, required: true }),
  }),
  primaryContact: z.boolean(),
});

export type DirectBeneficialOwnerControlPersonFormValues = z.infer<typeof directBeneficialOwnerControlPersonFormSchema>;

export const directBeneficialOwnerControlPersonFormDefaultValues = {
  kind: 'directBeneficialOwner',
  value: {
    profileId: '',
    ownership: '',
    signer: false,
  },
  primaryContact: false,
} as const satisfies DirectBeneficialOwnerControlPersonFormValues;

export function formValuesToDirectBeneficialOwnerControlPerson(
  values: DirectBeneficialOwnerControlPersonFormValues,
): IxcRegistrationV1BeneficialOwnerDirect {
  let ownership = 0;

  try {
    ownership = new Big(parseNumber(values.value.ownership)).div(100).toNumber();
  } catch {}

  return {
    profileId: values.value.profileId,
    ownership,
    signer: values.value.signer,
  };
}

export function getInitialFormValuesForDirectBeneficialOwnerControlPerson(
  data: IxcRegistrationV1BeneficialOwnerDirect | undefined,
  primaryContactId: string | undefined,
): DirectBeneficialOwnerControlPersonFormValues {
  return {
    ...directBeneficialOwnerControlPersonFormDefaultValues,
    value: {
      ...directBeneficialOwnerControlPersonFormDefaultValues.value,
      profileId: data?.profileId ?? '',
      ownership: data?.ownership ? new Big(data.ownership).mul(100).toString() : '',
      signer: Boolean(data?.signer),
    },
    primaryContact: Boolean(data?.profileId && data.profileId === primaryContactId),
  };
}

interface DirectBeneficialOwnerControlPersonFormProps {
  lens: Lens<DirectBeneficialOwnerControlPersonFormValues>;
  programId: string | undefined;
}

export function DirectBeneficialOwnerControlPersonControlPersonForm({ lens, programId }: DirectBeneficialOwnerControlPersonFormProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsElementVisible(ref);
  const profileSelectRequest = useMemo(() => {
    if (!programId) {
      return null;
    }

    return buildProfileListQuery({ programId, status: IxcRegistrationV1ProfileStatus.Active });
  }, [programId]);

  return (
    <VerticalStack gap={4} ref={ref}>
      <RegistrationProfileComboboxWithLens
        label="Profile"
        lens={lens.focus('value.profileId')}
        queryOptions={profileSelectRequest}
        required={isVisible}
      />

      <AppTextInputWithLens
        label="Ownership Percentage"
        lens={lens.focus('value.ownership')}
        maskOptions={DEFAULT_PERCENTAGE_MASK}
        required={isVisible}
      />

      <SwitchInputGroup>
        <AppSwitchInputWithLens label="Signer" lens={lens.focus('value.signer')} />
        <AppSwitchInputWithLens label="Primary Contact" lens={lens.focus('primaryContact')} />
      </SwitchInputGroup>
    </VerticalStack>
  );
}
